import React, { useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Button, Stack, Typography, Paper,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Edit as EditIcon, Delete as DeleteIcon, AttachFile as AttachFileIcon, Download as DownloadIcon } from '@mui/icons-material';

import { Container, LoadingState, ErrorState } from '@/components/common';
import { useLibraryDetail, useDeleteLibraryPost, useDownloadLibraryFile } from '@/hooks/boards.hook';
import { successToast, errorToast, confirmToast } from '@/common/toast';
import { formatDate } from '@/util/dateUtils';

/**
 * 자료실 상세 페이지
 */
const LibraryDetailPage = () => {
  const { libraryId } = useParams();
  const navigate = useNavigate();

  const { data: library, isLoading, error } = useLibraryDetail(libraryId);
  const deleteLibraryMutation = useDeleteLibraryPost();
  const downloadLibraryMutation = useDownloadLibraryFile();

  const handleDownload = useCallback(async () => {
    try {
      const res = await downloadLibraryMutation.mutateAsync(libraryId);
      const blob = res.data;
      const contentDisposition = res.headers?.['content-disposition'];
      let fileName = library?.file?.originalFileName ?? library?.file?.fileName ?? 'download';
      if (contentDisposition) {
        const filenameStarMatch = contentDisposition.match(/filename\*=UTF-8''([^;]*)/);
        if (filenameStarMatch && filenameStarMatch[1]) {
          fileName = decodeURIComponent(filenameStarMatch[1]);
        }
        else {
          const filenameMatch = contentDisposition.match(/filename="?([^";]*)"?/);
          if (filenameMatch && filenameMatch[1]) {
            fileName = filenameMatch[1].replace(/['"]/g, '');
          }
        }
      }
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();
      window.URL.revokeObjectURL(url);
      successToast('파일이 다운로드되었습니다.');
    } catch (err) {
      errorToast(`파일 다운로드에 실패했습니다. - ${err.response?.data?.msg}`);
    }
  }, [libraryId, library?.file?.originalFileName, library?.file?.fileName, downloadLibraryMutation.mutateAsync]);

  const handleDelete = useCallback(() => {
    confirmToast('이 자료를 삭제하시겠습니까?', async () => {
      try {
        await deleteLibraryMutation.mutateAsync(libraryId);
        successToast('자료가 삭제되었습니다.');
        navigate('/community/library');
      } catch (err) {
        errorToast(`자료 삭제에 실패했습니다. - ${err.response?.data?.msg}`);
      }
    });
  }, [libraryId, deleteLibraryMutation, navigate]);

  const handleEdit = useCallback(() => {
    navigate(`/community/library/edit/${libraryId}`);
  }, [libraryId, navigate]);

  if (isLoading) {
    return <LoadingState title="자료 로딩 중" />;
  }

  if (error || !library) {
    return (
      <ErrorState
        title="자료 로딩 오류"
        message="자료 정보를 불러오는 중 오류가 발생했습니다."
        onGoBack={() => navigate('/community/library')}
      />
    );
  }

  const title = library.title ?? library.name ?? '-';
  const content = library.content ?? library.description ?? '';
  const author = library.author ?? library.userId ?? '-';
  const categoryName = [library.category?.categoryType ?? library.categoryType, library.category?.categoryName ?? library.categoryName].filter(Boolean).join(' - ') || '-';
  const hitCount = library.hitCount ?? 0;
  const createdAt = library.createdAt ?? library.regDate ?? '-';
  const file = library.file ?? null;

  const formatFileSize = (bytes) => {
    if (bytes == null || bytes === 0) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <Container title="자료 상세">
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/community/library')}
        >
          목록으로
        </Button>
        <Stack direction="row" gap={1}>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={handleEdit}
          >
            수정
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDelete}
            disabled={deleteLibraryMutation.isPending}
          >
            삭제
          </Button>
        </Stack>
      </Stack>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          {title}
        </Typography>
        <Stack direction="row" gap={2} flexWrap="wrap" sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary">
            등록일: {formatDate(createdAt)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            작성자: {author}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            카테고리: {categoryName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            조회수: {hitCount}
          </Typography>
        </Stack>
        <Box
          sx={{
            py: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            whiteSpace: 'pre-wrap',
          }}
        >
          {content || '-'}
        </Box>
        {file && (
          <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              첨부파일
            </Typography>
            <Stack direction="row" alignItems="center" gap={1}>
              <AttachFileIcon fontSize="small" color="action" />
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={handleDownload}
                disabled={downloadLibraryMutation.isPending}
              >
                {file.originalFileName ?? file.fileName ?? '파일'}
              </Button>
              <Typography variant="body2" color="text.secondary">
                ({formatFileSize(file.size)})
              </Typography>
            </Stack>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default LibraryDetailPage;
